import { useState, useEffect, useCallback, useMemo } from "react";
import { notification, Button, Space } from "antd";
import { FormOutlined, BookOutlined } from "@ant-design/icons";
import "./KiwiManufactureList.scss";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import { formatDate, compareDates } from '../../utils/dates';
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { useGetVersionsLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function KiwiManufactureList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();

  // ===== 3. DERIVADOS (Computados) =====
  const selectedId = data.selected?.uuid || "";

  const versionFilter = versions
    .filter(v => v.device_type === 'kiwi')
    .map(v => ({ text: v.name, value: v.uuid }));

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("kiwi/manufacture/Fabricado");

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);

  const enrichedData = useMemo(() => {
    return data.raw.map((element) => {
      const version = versions.find(v => v.uuid === element.version_uuid);
      const version_name = version ? version.name : '-';

      const dateFormated = formatDate(element.date);

      return {
        ...element,
        version_name,
        dateFormated
      };
    });
  }, [data.raw, versions]);

  // ===== 6. HANDLERS =====
  const deletePlaca = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del(`kiwi/${selectedId}`, selectedId);
      if (res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res.data.message),
        });
      } else {
        notification["success"]({
          message: lang.handle('Placa borrada correctamente'),
        });
      }
      await Pfetch();
      updateData('selected', null);
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  };

  const updatePlaca = () => {
    updateUI('loading', true);
    navigate(`/boards/manufacture/edit/2/${selectedId}`);
  };

  const assignPlaca = () => {
    updateUI('loading', true);
    navigate(`/boards/assing/${selectedId}`);
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => {
      updateUI('loading', false);
    });
  }, [Pfetch]);

  // ===== 8. CONFIG (columnas, etc) =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    {
      title: "SSID",
      dataIndex: "ssid",
      ...GetColumnLiteralSearchProps("ssid", lang),
    },
    {
      title: "MAC",
      dataIndex: "mac",
      ...GetColumnLiteralSearchProps("mac", lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "dateFormated",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || ''),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: "notes",
    },
  ];

  let selection = null;
  if (hasPrivilege('manufacture_delete') || hasPrivilege('manufacture_edit') || hasPrivilege('assign')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selected?.uuid ? [data.selected.uuid] : [],
      onChange: onSelectionChange,
    };
  }

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={selection}
        columns={columns}
        rowKey="uuid"
        dataSource={enrichedData}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {hasPrivilege('manufacture_delete') && (
            <DeleteButton
              onEvent={deletePlaca}
              buttonDisabled={!data.selected}
            />
          )}

          {hasPrivilege('manufacture_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={updatePlaca}
            >
              {lang.handle('Editar')}
            </Button>
          )}

          {hasPrivilege('assign') && (
            <Button
              type="primary"
              icon={<BookOutlined />}
              disabled={!data.selected}
              onClick={assignPlaca}
            >
              {lang.handle('Asignar')}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(KiwiManufactureList));
